import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { MoveHorizontal } from 'lucide-react';

export const BeforeAfterSlider: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = useCallback(() => {
    isDragging.current = true;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    handleMove(e.clientX);
  }, [handleMove]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  return (
    <section id="beforeafter" className="relative py-24 md:py-32 bg-vilarias-dark overflow-hidden">
      <div className="absolute inset-0 bg-hero-glow opacity-20" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.beforeAfter.headline}
          </h2>
          <p className="text-vilarias-text-secondary max-w-xl mx-auto">
            {t.beforeAfter.description}
          </p>
          <div className="h-px w-16 bg-vilarias-accent mx-auto mt-6" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div
            ref={containerRef}
            className="relative aspect-[16/9] rounded-2xl overflow-hidden cursor-ew-resize select-none glass-card"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
          >
            {/* After image (AI visualization) */}
            <div className="absolute inset-0 bg-gradient-to-br from-vilarias-accent/30 via-vilarias-dark to-vilarias-black">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full border-2 border-vilarias-accent/50 flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                    <SparklesIcon />
                  </div>
                  <p className="text-white font-serif text-xl tracking-wider">AI VISUALIZATION</p>
                  <p className="text-vilarias-accent text-sm mt-2">Premium Render</p>
                </div>
              </div>
            </div>

            {/* Before image (empty land) */}
            <div
              className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-vilarias-dark to-vilarias-black overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full border-2 border-amber-600/50 flex items-center justify-center mx-auto mb-4">
                    <LandIcon />
                  </div>
                  <p className="text-white font-serif text-xl tracking-wider">EMPTY LAND</p>
                  <p className="text-amber-500/70 text-sm mt-2">Before</p>
                </div>
              </div>
            </div>

            {/* Slider line */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_20px_rgba(0,141,255,0.5)] z-10"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-vilarias-accent border-2 border-white shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown}
                onTouchStart={handleMouseDown}
              >
                <MoveHorizontal size={16} className="text-white" />
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/50 text-xs text-amber-400 border border-amber-500/30">
              Before
            </div>
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/50 text-xs text-vilarias-glow border border-vilarias-accent/30">
              After
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

function SparklesIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#008DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    </svg>
  );
}

function LandIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" />
    </svg>
  );
}
